import { ReactElement, useMemo } from 'react';
import { DataMode, buildRatioAccelScatter } from './auxSensorChannels';
import { DEFAULT_PAIR_LABEL } from './auxPlotStyle';
import AuxRatioScatterCard, { RatioScatterAxis } from './AuxRatioScatterCard';

interface AuxRatioAccelPlotProps {
  gridded: Record<string, (number | null)[]> | undefined;
  circumference: number;
  /** Min linear speed (m/s) — samples below are masked (shared slider). */
  threshold: number;
  dataMode?: DataMode;
  isLoading?: boolean;
  /** Names the two wheel sensors the ratio is between — see AuxRatioTimePlot. */
  pairLabel?: string;
}

/** Acceleration fit-window slider, in m/s²: its range, step, and starting window. */
const ACCEL_SLIDER_MIN = -1;
const ACCEL_SLIDER_MAX = 0.6;
const ACCEL_SLIDER_STEP = 0.01;
const ACCEL_INITIAL_WINDOW = { low: 0, high: 0.3 };

/**
 * The driving-force plot with acceleration (dv/dt, m/s²) on the x-axis in place of
 * tyre driving force — same masking, fit, fit window and "Zoom to window". The fit
 * window slider runs −1 to 0.6 m/s² and starts at 0–0.3 m/s². See `buildRatioAccelScatter`
 * for where the acceleration comes from.
 */
export default function AuxRatioAccelPlot({
  gridded,
  circumference,
  threshold,
  dataMode = 'all',
  isLoading = false,
  pairLabel = DEFAULT_PAIR_LABEL,
}: AuxRatioAccelPlotProps): ReactElement {
  const scatter = useMemo(
    () => buildRatioAccelScatter(gridded, circumference, threshold, dataMode),
    [gridded, circumference, threshold, dataMode],
  );
  const axis = useMemo<RatioScatterAxis>(() => ({
    title: scatter.source === 'wheel'
      ? 'Acceleration (m/s², from wheel 1 speed — no GPS)'
      : 'Acceleration (m/s², from GPS speed)',
    unit: 'm/s²',
    tooltipDigits: 3,
    fitWindow: {
      label: 'Acceleration window',
      min: ACCEL_SLIDER_MIN,
      max: ACCEL_SLIDER_MAX,
      step: ACCEL_SLIDER_STEP,
      digits: 2,
      initial: ACCEL_INITIAL_WINDOW,
    },
    format: { slopeUnit: '/(m/s²)', slopeDigits: 4, offsetDigits: 4 },
  }), [scatter.source]);

  return (
    <AuxRatioScatterCard
      title="Slip Ratio vs Acceleration"
      storageKey="ratioAccel"
      scatter={scatter}
      axis={axis}
      threshold={threshold}
      pairLabel={pairLabel}
      emptyMessage="No slip-ratio samples for this run (needs two wheel sensors)."
      isLoading={isLoading}
    />
  );
}
